import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	getNewNotePayload,
	getTypingPayload,
	getUpdatedTypingDetails,
	notify,
	WEB_SOCKET_MESSAGE_TYPES,
} from '../../utils/WebSocketUtils';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import { getContractorOrWorkOrderId } from '../../utils/WorkOrderUtils';

const TYPING_EVENT_INTERVAL = 2000;
const TYPING_STOP_INACTIVITY_THRESHOLD = 2000;
const TITLE_BLINKING_INTERVAL = 1000;
export default class WebSocketTypingManager {
	previousSocket: any;
	cleanupInterval: any;
	component: any;
	socketProvider: any;
	typing = false;
	blinkInterval = null;
	originalTitle = document.title;
	isTabActive = true;

	constructor({ component, socketProvider }) {
		this.component = component;
		this.socketProvider = socketProvider;
	}

	_sendTypingEvent = () => {
		this.sendTyping();
	};

	_sendTypingStopEvent = () => {
		this.typing = false;
		this.sendTypingStop();
	};

	throttledSendTyping = throttle(
		() => {
			this._sendTypingEvent();
		},
		TYPING_EVENT_INTERVAL,
		{ leading: true, trailing: false }
	);

	debouncedStopTyping = debounce(() => {
		this._sendTypingStopEvent();
	}, TYPING_STOP_INACTIVITY_THRESHOLD);

	startBlinkingTitle = (message = 'New Notification') => {
		if (this.isTabActive) return;
		if (this.blinkInterval) return; // avoid duplicates

		let toggle = false;

		this.blinkInterval = setInterval(() => {
			document.title = toggle ? `${message} 🔔` : this.originalTitle;
			toggle = !toggle;
		}, TITLE_BLINKING_INTERVAL);
	};

	stopBlinkingTitle = () => {
		if (this.blinkInterval) {
			clearInterval(this.blinkInterval);
			this.blinkInterval = null;
		}
		document.title = this.originalTitle;
	};

	setupVisibilityListener = () => {
		document.addEventListener('visibilitychange', this.handleVisibilityChange);
	};

	cleanupVisibilityListener = () => {
		document.removeEventListener('visibilitychange', this.handleVisibilityChange);
	};

	handleVisibilityChange = () => {
		this.isTabActive = document.visibilityState === 'visible';

		if (this.isTabActive) {
			this.stopBlinkingTitle();
		}
	};

	attachListener = () => {
		const socketContext = this.socketProvider();
		const { socket } = socketContext;

		if (!socket) return;

		if (this.previousSocket !== socket) {
			if (this.previousSocket) {
				this.previousSocket.removeEventListener('message', this.handleMessage);
			}
			socket.removeEventListener('message', this.handleMessage);
			socket.addEventListener('message', this.handleMessage);
			this.previousSocket = socket;
		}
	};

	getUpdatedTypingDetailsWithMessages = (prevState, updatedTypingDetails) => {
		const messages = nullSafeGetOrElse('messages', prevState, []);

		const messagesWithoutTypingDetails = messages.filter((_) => _.type !== 'typing');
		return {
			typingDetails: updatedTypingDetails,
			messages: [
				...messagesWithoutTypingDetails,
				...updatedTypingDetails.map((td) => ({
					type: 'typing',
					text: 'typing...',
					noteAddedByContact: td,
				})),
			],
		};
	};

	startCleanup = () => {
		if (this.cleanupInterval) clearInterval(this.cleanupInterval);

		this.cleanupInterval = setInterval(() => {
			this.component.setState((prev) => {
				const updatedTypingDetails = getUpdatedTypingDetails(prev.typingDetails, null);
				return this.getUpdatedTypingDetailsWithMessages(prev, updatedTypingDetails);
			});
		}, 1000);
	};

	stopCleanup = () => {
		clearInterval(this.cleanupInterval);
		this.cleanupInterval = null;
	};

	handleMessage = (event) => {
		const { component } = this;

		try {
			const data = JSON.parse(event.data);

			const workOrderId = getContractorOrWorkOrderId(nullSafeGet('props.workOrder', component));
			if (data.workOrderId != workOrderId) return;

			switch (data.messageType) {
				case WEB_SOCKET_MESSAGE_TYPES.NEW_WO_NOTE:
					notify();
					this.startBlinkingTitle('New Note');
					component.refreshMessages && component.refreshMessages();
					break;

				case WEB_SOCKET_MESSAGE_TYPES.WO_NOTE_TYPING:
					component.setState(
						(prev) => {
							const updatedTypingDetails = getUpdatedTypingDetails(
								prev.typingDetails,
								nullSafeGet('data.typingBy', data)
							);
							return this.getUpdatedTypingDetailsWithMessages(prev, updatedTypingDetails);
						},
						() => this.component.scrollToTop()
					);
					break;
				case WEB_SOCKET_MESSAGE_TYPES.WO_NOTE_TYPING_STOP:
					component.setState((prev) => {
						const updatedTypingDetails = prev.typingDetails.filter(
							(detail) => detail.email !== nullSafeGet('data.typingBy.email', data)
						);
						return this.getUpdatedTypingDetailsWithMessages(prev, updatedTypingDetails);
					});
					break;

				default:
					break;
			}
		} catch (err) {
			console.log('JSON parse error', err);
		}
	};

	sendTypingEvent = (messageType) => {
		const { workOrder, currentUser } = this.component.props;

		const broadCastData = getTypingPayload(workOrder, currentUser, messageType);

		const socketContext = this.socketProvider();
		const { sendMessage } = socketContext;

		if (sendMessage) {
			sendMessage(broadCastData);
		}
	};

	sendTyping = () => {
		this.sendTypingEvent(WEB_SOCKET_MESSAGE_TYPES.WO_NOTE_TYPING);
	};

	sendTypingStop = () => {
		this.sendTypingEvent(WEB_SOCKET_MESSAGE_TYPES.WO_NOTE_TYPING_STOP);
	};

	handleTyping = () => {
		if (!this.typing) {
			this.typing = true;
			this._sendTypingEvent();
		}

		this.throttledSendTyping();
		this.debouncedStopTyping();
	};

	onSendNote = () => {
		const { workOrder, currentUser } = this.component.props;

		const broadCastData = getNewNotePayload(workOrder, currentUser);

		const socketContext = this.socketProvider();
		const { sendMessage } = socketContext;

		if (sendMessage) {
			sendMessage(broadCastData);
		}
	};

	onMount = () => {
		this.attachListener();
		this.startCleanup();
		this.setupVisibilityListener();
	};

	onUpdate = () => {
		this.attachListener();
	};

	onUnmount = () => {
		const socketContext = this.socketProvider();
		const { socket } = socketContext;
		if (socket) {
			socket.removeEventListener('message', this.handleMessage);
		}
		this.previousSocket = null;
		this.stopCleanup();
		this.stopBlinkingTitle();
		this.cleanupVisibilityListener();
	};
}
